import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { Box, Tooltip } from '@mui/material';
import { EmployeeLinkWithNameLoading } from 'domain/shared/employee';
import { CountryBadge } from 'framework/form';
import type { FlatListTableProps, ListTableColumn } from 'framework/ui/ui-builder';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import VendorCompliantBadge from '../shared/fields/VendorCompliantBadge';
import VendorProfileStatusBadge from '../shared/fields/VendorProfileStatusBadge';
import VendorWorkingStatusBadge from '../shared/fields/VendorWorkingStatusBadge';
import type { VendorDto } from '../types';

interface VendorsTableActions {
  onCreate: () => void;
}

const getColumns = (getDetailsUrl: (id: string) => string): ListTableColumn<VendorDto>[] => [
  {
    id: 'vendorName',
    label: 'Name',
    width: 520,
    render: vendor => (
      <Box display="flex" alignItems="center">
        <Link to={getDetailsUrl(vendor.id)}>{vendor.vendorName || vendor.id}</Link>
        <Box display="flex" alignItems="center" flexShrink={0}>
          <VendorProfileStatusBadge status={vendor.profileStatus} />
          <VendorWorkingStatusBadge status={vendor.workingStatus} />
          <VendorCompliantBadge isCompliant={vendor.isCompliant} />
        </Box>
      </Box>
    ),
  },
  {
    id: 'owner',
    label: 'Owner',
    width: 280,
    render: vendor =>
      vendor.owner ? (
        <EmployeeLinkWithNameLoading id={vendor.owner} defaultValue={vendor.owner} />
      ) : (
        EMPTY_VALUE_INDICATOR
      ),
  },
  {
    id: 'country',
    label: 'Country',
    width: 120,
    render: vendor =>
      vendor.country ? (
        <Tooltip title={vendor.country}>
          <span>
            <CountryBadge country={vendor.country} />
          </span>
        </Tooltip>
      ) : (
        EMPTY_VALUE_INDICATOR
      ),
  },
];

export const getVendorsTableConfig = (
  rows: VendorDto[],
  getDetailsUrl: (id: string) => string,
  { onCreate }: VendorsTableActions,
): FlatListTableProps<VendorDto> => ({
  rows,
  columns: getColumns(getDetailsUrl),
  mainAction: {
    id: 'create-vendor',
    label: 'Create vendor',
    tooltip: 'Create vendor',
    icon: <AddIcon />,
    variant: 'contained',
    onClick: onCreate,
  },
  emptyContent: 'No vendors found.',
});
